import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Table, Button, Form, Badge } from 'react-bootstrap';
import { bomAPI } from '../services/api';
import './BOMDataDisplay.css';

const MATERIAL_TYPE_OPTIONS = [
  'Driver',
  'Optic',
  'LED Board',
  'SPD',
  'Controls',
  'Cover',
  'Gasket',
  'Mount',
  'Connectors',
  'Clips',
  'Other',
];

const BOMDataDisplay = ({
  bomData,
  csvData,
  onUploadSuccess,
  showCreateBOMButton = true,
  isStandaloneMode = false,
  previewOnly = false,
  dialogOnly = false,
  externalCreateBOMTrigger = 0,
  externalCreateBOM12NC = '',
  externalCreateBOMFactoryCode = '',
  uploadedCSVFiles = [],
  uploadedBOMFiles = [],
  uploadedBOMData = [],
  supportedFactoryCodes = [],
  factoryBOMRows = [],
}) => {
  const [localBomData, setLocalBomData] = useState(bomData);
  const [showCreateBOMDialog, setShowCreateBOMDialog] = useState(false);
  const [newBOMEntries, setNewBOMEntries] = useState([{ name: '', materialtype: '', type: '' }]); // For dialog form
  const [luminaire12NC, setLuminaire12NC] = useState(''); // Luminaire 12NC for file naming
  const [newBOMEffectiveDate, setNewBOMEffectiveDate] = useState('');
  const [selectedSupportedFactoryCode, setSelectedSupportedFactoryCode] = useState('');
  const [uploadToast, setUploadToast] = useState({ show: false, message: '', type: 'success' }); // Toast for upload notifications
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded table rows
  const [manualFactoryCodeBy12NC, setManualFactoryCodeBy12NC] = useState({});
  const [createBOMStep, setCreateBOMStep] = useState(1);
  const [createBOMResult, setCreateBOMResult] = useState({ status: 'idle', fileName: '', errorMessage: '' });
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering BOM data
  const [isSubmittingBOM, setIsSubmittingBOM] = useState(false);
  // Lock 12NC + Factory Code when the dialog is opened from a specific table row.
  const [isCreateBOMFromTable, setIsCreateBOMFromTable] = useState(false);

  const normalizedSupportedFactoryCodes = useMemo(() => (
    Array.from(
      new Set(
        (Array.isArray(supportedFactoryCodes) ? supportedFactoryCodes : [])
          .map((code) => String(code || '').trim())
          .filter(Boolean)
      )
    )
  ), [supportedFactoryCodes]);

  const hasMultipleSupportedFactoryCodes = normalizedSupportedFactoryCodes.length > 1;

  const maxEffectiveDate = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return `${currentYear}-12-31`;
  }, []);

  // Auto-dismiss upload toast after 5 seconds
  useEffect(() => {
    if (uploadToast.show) {
      const timer = setTimeout(() => {
        setUploadToast({ show: false, message: '', type: 'success' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [uploadToast.show]);

  // Keep local rows aligned with the latest parsed CSV/BOM payload.
  useEffect(() => {
    setLocalBomData(bomData || {});
  }, [bomData]);
  const globalBOMFiles = useMemo(() => (
    Array.isArray(uploadedBOMData) ? uploadedBOMData : []
  ), [uploadedBOMData]);

  useEffect(() => {
    if (isStandaloneMode || !externalCreateBOMTrigger) {
      return;
    }

    const preferred12NC = String(externalCreateBOM12NC || '').trim();
    const available12NCs = getAllUnique12NCs();
    if (isValidLuminaire12NC(preferred12NC) && (available12NCs.length === 0 || available12NCs.includes(preferred12NC))) {
      setLuminaire12NC(preferred12NC);
    } else if (available12NCs.length > 0) {
      setLuminaire12NC(available12NCs[0]);
    } else {
      setLuminaire12NC('');
    }

    // When launched from a table row, auto-populate and lock the factory code too.
    const preferredFactoryCode = String(externalCreateBOMFactoryCode || '').trim();
    const isFromTableRow = isValidLuminaire12NC(preferred12NC) && !!preferredFactoryCode;
    if (isFromTableRow) {
      setSelectedSupportedFactoryCode(preferredFactoryCode);
    }
    setIsCreateBOMFromTable(isFromTableRow);

    setShowCreateBOMDialog(true);
  }, [externalCreateBOMTrigger, externalCreateBOM12NC, externalCreateBOMFactoryCode, isStandaloneMode]);

  const addNewBOMEntry = () => {
    setNewBOMEntries(prev => [...prev, { name: '', materialtype: '', type: '' }]);
  };

  const removeNewBOMEntry = (index) => {
    if (newBOMEntries.length > 1) {
      setNewBOMEntries(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateNewBOMEntry = (index, field, value) => {
    setNewBOMEntries(prev =>
      prev.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  // Extract all unique 12NCs from all service tags in CSV data
  const getAllUnique12NCs = () => {
    if (!csvData || !csvData.originalCsvData || !csvData.serviceTagColumn) {
      return [];
    }

    const unique12NCs = new Set();
    csvData.originalCsvData.forEach(row => {
      const serviceTag = row[csvData.serviceTagColumn];
      if (serviceTag) {
        const nc12 = extract12NCFromServiceTag(serviceTag);
        if (isValidLuminaire12NC(nc12)) {
          unique12NCs.add(nc12);
        }
      }
    });

    return Array.from(unique12NCs).sort();
  };

  // Extract 12NC from service tag format: 12nc.manufacturingDate.FactoryCode.BatchId.SerialNum
  const extract12NCFromServiceTag = (serviceTag) => {
    if (!serviceTag) return '';
    const parts = serviceTag.split('.');
    return parts.length > 0 ? String(parts[0]).trim() : '';
  };

  const isValidLuminaire12NC = (value) => /^\d{12}$/.test(String(value || '').trim());

  const handleLuminaire12NCChange = (event) => {
    const digitsOnlyValue = event.target.value.replace(/\D/g, '').slice(0, 12);
    setLuminaire12NC(digitsOnlyValue);
  };

  const getBatch12NCMap = () => {
    const batch12NCMap = {};

    if (!csvData || !Array.isArray(csvData.originalCsvData) || !csvData.serviceTagColumn) {
      return batch12NCMap;
    }

    csvData.originalCsvData.forEach((row) => {
      const batchId = row?.extractedBatchId;
      if (!batchId || batch12NCMap[batchId]) {
        return;
      }

      const serviceTag = row[csvData.serviceTagColumn];
      const extracted12NC = extract12NCFromServiceTag(serviceTag);

      if (extracted12NC) {
        batch12NCMap[batchId] = extracted12NC;
      }
    });

    return batch12NCMap;
  };

  const batch12NCMap = getBatch12NCMap();

  const twelveNCFactoryCodeMap = useMemo(() => {
    const factoryCodeMap = {};

    if (!csvData || !Array.isArray(csvData.originalCsvData)) {
      return factoryCodeMap;
    }

    csvData.originalCsvData.forEach((row) => {
      const twelveNC = String(row?.extracted12NC || '').trim();
      const factoryCode = String(row?.extractedFactoryCode || '').trim();

      if (twelveNC && factoryCode && !factoryCodeMap[twelveNC]) {
        factoryCodeMap[twelveNC] = factoryCode;
      }
    });

    return factoryCodeMap;
  }, [csvData]);

  const twelveNCManufacturingDateMap = useMemo(() => {
    const manufacturingDateMap = {};

    if (!csvData || !Array.isArray(csvData.originalCsvData)) {
      return manufacturingDateMap;
    }

    csvData.originalCsvData.forEach((row) => {
      const twelveNC = String(row?.extracted12NC || '').trim();
      const manufacturingDate = String(row?.extractedManufacturingDate || '').trim();

      if (twelveNC && manufacturingDate && !manufacturingDateMap[twelveNC]) {
        manufacturingDateMap[twelveNC] = manufacturingDate;
      }
    });

    return manufacturingDateMap;
  }, [csvData]);

  const extract12NCFromBOMFileName = useCallback((fileName) => {
    if (!fileName) {
      return '';
    }

    const normalizedName = String(fileName).trim().replace(/\.json$/i, '');
    const match = normalizedName.match(/^BOM-(\d{12})-.+$/i);
    return match ? match[1] : '';
  }, []);

  const resolveBOMFile12NC = useCallback((bomFile) => {
    const luminaire12NC = String(bomFile?.luminaire12NC || '').trim();
    if (isValidLuminaire12NC(luminaire12NC)) {
      return luminaire12NC;
    }

    return extract12NCFromBOMFileName(bomFile?.name);
  }, [extract12NCFromBOMFileName]);

  const getDefaultFactoryCode = useCallback(() => {
    return normalizedSupportedFactoryCodes.length === 1 ? normalizedSupportedFactoryCodes[0] : '';
  }, [normalizedSupportedFactoryCodes]);

  const handleManualFactoryCodeChange = (twelveNC, factoryCode) => {
    setManualFactoryCodeBy12NC((previous) => ({
      ...previous,
      [twelveNC]: factoryCode,
    }));
  };

  const parseComparableDate = (value) => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return null;
    }

    // Service tag date format: DDMMYYYY
    if (/^\d{8}$/.test(normalizedValue)) {
      const day = Number(normalizedValue.slice(0, 2));
      const month = Number(normalizedValue.slice(2, 4));
      const year = Number(normalizedValue.slice(4, 8));

      const timestamp = Date.UTC(year, month - 1, day);
      if (!Number.isNaN(timestamp)) {
        return timestamp;
      }
    }

    // BOM / factory effective date format: YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
      const [year, month, day] = normalizedValue.split('-').map(Number);
      const timestamp = Date.UTC(year, month - 1, day);
      if (!Number.isNaN(timestamp)) {
        return timestamp;
      }
    }

    const fallbackTimestamp = new Date(normalizedValue).getTime();
    return Number.isNaN(fallbackTimestamp) ? null : fallbackTimestamp;
  };

  const isValidISODate = (value) => {
    const normalizedValue = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
      return false;
    }

    const [year, month, day] = normalizedValue.split('-').map(Number);
    const utcDate = new Date(Date.UTC(year, month - 1, day));

    // Ensure the input maps to a real calendar date and was not auto-corrected.
    return (
      utcDate.getUTCFullYear() === year
      && utcDate.getUTCMonth() + 1 === month
      && utcDate.getUTCDate() === day
    );
  };

  const formatServiceTagDateForDisplay = (value) => {
    const normalizedValue = String(value || '').trim();
    if (!/^\d{8}$/.test(normalizedValue)) {
      return normalizedValue;
    }

    const day = normalizedValue.slice(0, 2);
    const month = normalizedValue.slice(2, 4);
    const year = normalizedValue.slice(4, 8);
    return `${day}/${month}/${year}`;
  };

  const formatEffectiveDateForDisplay = (value) => {
    const normalizedValue = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
      return normalizedValue;
    }

    const [year, month, day] = normalizedValue.split('-');
    return `${day}/${month}/${year}`;
  };

  const chooseNearestEffectiveDate = (manufacturingDate, candidateDates) => {
    if (!manufacturingDate || candidateDates.length === 0) {
      return '';
    }

    const manufacturingTimestamp = parseComparableDate(manufacturingDate);
    if (manufacturingTimestamp === null) {
      return '';
    }

    const datedCandidates = candidateDates
      .map((effectiveDate) => ({
        effectiveDate,
        timestamp: parseComparableDate(effectiveDate),
      }))
      .filter((entry) => entry.timestamp !== null);

    if (datedCandidates.length === 0) {
      return '';
    }

    // Prefer latest effective date that is on/before manufacturing date.
    const onOrBeforeCandidates = datedCandidates
      .filter((entry) => entry.timestamp <= manufacturingTimestamp)
      .sort((left, right) => right.timestamp - left.timestamp);

    if (onOrBeforeCandidates.length > 0) {
      return onOrBeforeCandidates[0].effectiveDate;
    }

    // If all effective dates are in the future, this manufacturing date has no valid match.
    return '';
  };

  // Build table rows from CSV 12NCs, enriched with Factory BOM Details API data and in-memory BOM uploads.
  const tableRows = useMemo(() => {
    // Build lookup from factory BOM API data grouped by 12NC.
    const factoryBOMByNC = {};
    (factoryBOMRows || []).forEach((row) => {
      const nc = String(row?.twelveNC || '').trim();
      if (!nc) {
        return;
      }

      if (!factoryBOMByNC[nc]) {
        factoryBOMByNC[nc] = [];
      }

      factoryBOMByNC[nc].push(row);
    });

    const hasCSVData = Array.isArray(csvData?.originalCsvData) && csvData.originalCsvData.length > 0;

    // Unique 12NCs from CSV (primary source), preserving first-seen CSV order.
    const csv12NCs = [];
    const csvGroupsByNC = {};
    if (hasCSVData) {
      const seenCSV12NC = new Set();
      csvData.originalCsvData.forEach((row) => {
        const nc = String(row?.extracted12NC || '').trim();
        if (!nc || seenCSV12NC.has(nc)) {
          return;
        }

        seenCSV12NC.add(nc);
        csv12NCs.push(nc);
      });

      csvData.originalCsvData.forEach((row) => {
        const nc = String(row?.extracted12NC || '').trim();
        const manufacturingDate = String(row?.extractedManufacturingDate || '').trim();

        if (!nc) {
          return;
        }

        if (!csvGroupsByNC[nc]) {
          csvGroupsByNC[nc] = [];
        }

        const groupKey = manufacturingDate || '-';
        let group = csvGroupsByNC[nc].find((entry) => entry.groupKey === groupKey);

        if (!group) {
          group = {
            groupKey,
            manufacturingDate,
          };
          csvGroupsByNC[nc].push(group);
        }
      });
    }

    // Uploaded BOMs grouped by 12NC, newest first.
    const uploadedBOMByNC = {};
    globalBOMFiles.forEach((file) => {
      const nc = String(resolveBOMFile12NC(file) || '').trim();
      if (!nc) {
        return;
      }

      if (!uploadedBOMByNC[nc]) {
        uploadedBOMByNC[nc] = [];
      }

      uploadedBOMByNC[nc].push(file);
    });

    Object.keys(uploadedBOMByNC).forEach((nc) => {
      uploadedBOMByNC[nc].sort((a, b) => {
        const aTime = new Date(a?.createdAt || 0).getTime();
        const bTime = new Date(b?.createdAt || 0).getTime();
        return bTime - aTime;
      });
    });

    // The batch table should be driven by service tags and uploaded BOM files.
    const allTwelveNCs = [...csv12NCs];
    Object.keys(uploadedBOMByNC).forEach((nc) => {
      if (!allTwelveNCs.includes(nc)) {
        allTwelveNCs.push(nc);
      }
    });

    const builtRows = [];

    allTwelveNCs.forEach((twelveNC) => {
      const factoryRows = factoryBOMByNC[twelveNC] || [];
      const uploadedRows = uploadedBOMByNC[twelveNC] || [];
      const latestUploadedBOM = uploadedRows[0] || null;
      const csvFactoryCode = twelveNCFactoryCodeMap[twelveNC] || '';
      const hasServiceTag = csv12NCs.includes(twelveNC);
      const csvGroups = hasServiceTag
        ? (csvGroupsByNC[twelveNC] && csvGroupsByNC[twelveNC].length > 0
          ? csvGroupsByNC[twelveNC]
          : [{ groupKey: '-', manufacturingDate: '' }])
        : [{ groupKey: '-', manufacturingDate: '' }];

      const factoryRowsByDate = {};
      factoryRows.forEach((row) => {
        const factoryDate = String(row?.effectiveDate || '').trim();
        if (!factoryDate) {
          return;
        }

        if (!factoryRowsByDate[factoryDate]) {
          factoryRowsByDate[factoryDate] = [];
        }
        factoryRowsByDate[factoryDate].push(row);
      });

      const uploadedRowsByDate = {};
      uploadedRows.forEach((row) => {
        const effectiveDate = String(row?.effectiveDate || '').trim();
        if (!effectiveDate) {
          return;
        }

        if (!uploadedRowsByDate[effectiveDate]) {
          uploadedRowsByDate[effectiveDate] = [];
        }
        uploadedRowsByDate[effectiveDate].push(row);
      });

      const candidateEffectiveDates = Array.from(new Set([
        ...Object.keys(factoryRowsByDate),
        ...Object.keys(uploadedRowsByDate),
      ])).sort((left, right) => {
        const leftTime = parseComparableDate(left) || 0;
        const rightTime = parseComparableDate(right) || 0;
        return leftTime - rightTime;
      });

      csvGroups.forEach((group, groupIndex) => {
        const manufacturingDate = String(group?.manufacturingDate || '').trim();
        const selectedEffectiveDate = hasServiceTag
          ? chooseNearestEffectiveDate(manufacturingDate, candidateEffectiveDates)
          : String(latestUploadedBOM?.effectiveDate || '').trim();
        const selectedEffectiveDateDisplay = formatEffectiveDateForDisplay(selectedEffectiveDate);

        const matchedFactoryRows = selectedEffectiveDate ? (factoryRowsByDate[selectedEffectiveDate] || []) : [];
        const matchedUploadedRows = selectedEffectiveDate ? (uploadedRowsByDate[selectedEffectiveDate] || []) : [];
        const matchedFactoryRow = matchedFactoryRows[0] || null;
        const matchedUploadedBOM = matchedUploadedRows[0] || null;
        const fallbackFactoryRow = matchedFactoryRow || factoryRows[0] || null;

        const hasFactoryEntryForSelectedDate = matchedFactoryRows.length > 0;
        const hasServerBOMForSelectedDate = matchedFactoryRows.some((row) => row.data !== null && row.data !== undefined);

        let effectiveDate = hasServiceTag
          ? (formatServiceTagDateForDisplay(manufacturingDate) || '-')
          : '-';
        let factoryCode = hasServiceTag
          ? csvFactoryCode
          : String(fallbackFactoryRow?.factoryCode || '').trim();
        let statusMessage = `Upload a BOM file with effective date on or before manufacturing date (${formatServiceTagDateForDisplay(manufacturingDate)}).`;
        let allowFactorySelection = false;
        const bomFromAPI = hasServerBOMForSelectedDate;

        if (!factoryCode) {
          factoryCode = manualFactoryCodeBy12NC[twelveNC] || getDefaultFactoryCode();
          allowFactorySelection = !factoryCode;
        }

        if (hasServiceTag) {
          if (hasServerBOMForSelectedDate) {
            statusMessage = `BOM uploaded with effective date ${selectedEffectiveDateDisplay}`;
          } else if (matchedUploadedBOM) {
            statusMessage = `Matched uploaded BOM effective date ${selectedEffectiveDateDisplay}`;
          } else {
            statusMessage = `No uploaded BOM for servicetags`;
          }
        } else if (hasServerBOMForSelectedDate) {
          statusMessage = 'BOM File Already Uploaded with effective date ' + selectedEffectiveDateDisplay;
        } else if (matchedUploadedBOM) {
          statusMessage = `Upload Factory File to make use of this BOM ${selectedEffectiveDateDisplay}`;
        } else if (latestUploadedBOM) {
          effectiveDate = formatEffectiveDateForDisplay(String(latestUploadedBOM?.effectiveDate || '-').trim()) || '-';
          statusMessage = 'Waiting for Service Tags upload';
        }

        const hasMatchedBOM = bomFromAPI || !!matchedUploadedBOM;
        const hasFactoryDateMismatch = !!(matchedUploadedBOM && !hasFactoryEntryForSelectedDate);

        builtRows.push({
          rowKey: `${twelveNC}-${group.groupKey}-${selectedEffectiveDate || 'none'}-${groupIndex}`,
          twelveNC,
          manufacturingDate: effectiveDate,
          manufacturingDateRaw: manufacturingDate,
          selectedEffectiveDate,
          factoryCode,
          hasMatchedBOM,
          bomFromAPI,
          selectedBOMFile: matchedUploadedBOM?.name || null,
          bomEntries: matchedUploadedBOM ? (matchedUploadedBOM.bom || []) : [],
          uploadedBOMCount: uploadedRows.length,
          allowFactorySelection,
          statusMessage,
          hasFactoryDateMismatch,
        });
      });
    });

    return builtRows;
  }, [
    csvData,
    factoryBOMRows,
    globalBOMFiles,
    getDefaultFactoryCode,
    manualFactoryCodeBy12NC,
    resolveBOMFile12NC,
    twelveNCFactoryCodeMap,
  ]);

  const hasUploadedData = useMemo(() => {
    const hasCSVRows = Array.isArray(csvData?.originalCsvData) && csvData.originalCsvData.length > 0;
    const hasUploadedBOMRows = globalBOMFiles.length > 0;
    const hasFactoryRows = Array.isArray(factoryBOMRows) && factoryBOMRows.length > 0;

    return hasCSVRows || hasUploadedBOMRows || hasFactoryRows;
  }, [csvData, factoryBOMRows, globalBOMFiles]);

  const filteredTableRows = useMemo(() => {
    if (!searchTerm.trim()) {
      return tableRows;
    }

    const lowerCaseSearch = searchTerm.toLowerCase().trim();
    return tableRows.filter((row) => {
      const twelveNC = String(row.twelveNC || '').toLowerCase();
      const manufacturingDate = String(row.manufacturingDate || '').toLowerCase();
      const factoryCode = String(row.factoryCode || '').toLowerCase();
      const statusMessage = String(row.statusMessage || '').toLowerCase();
      const selectedBOMFile = String(row.selectedBOMFile || '').toLowerCase();

      return (
        twelveNC.includes(lowerCaseSearch) ||
        manufacturingDate.includes(lowerCaseSearch) ||
        factoryCode.includes(lowerCaseSearch) ||
        statusMessage.includes(lowerCaseSearch) ||
        selectedBOMFile.includes(lowerCaseSearch)
      );
    });
  }, [tableRows, searchTerm]);

  const hasSelectedFiles = uploadedCSVFiles.length > 0 || uploadedBOMFiles.length > 0;
  const showManagementWorkspace = !isStandaloneMode && (previewOnly || hasSelectedFiles);
  const showUploadSection = !previewOnly;
  const containerClassName = previewOnly ? 'bom-container bom-container-compact' : 'bom-container';
  const mainContentClassName = previewOnly ? 'bom-main-content bom-main-content-compact' : 'bom-main-content';



  const getBatch12NCMismatchWarnings = () => {
    const warnings = {};
    const filesById = globalBOMFiles.reduce((acc, file) => {
      acc[file.id] = file;
      return acc;
    }, {});

    const getUploadedLuminaire12NC = (batch, selectedFile) => {
      const selectedFileLuminaire12NC = String(selectedFile?.luminaire12NC || '').trim();
      if (selectedFileLuminaire12NC) {
        return selectedFileLuminaire12NC;
      }

      if (!batch?.rawBOMContent) {
        return '';
      }

      try {
        const parsed = JSON.parse(batch.rawBOMContent);

        if (parsed && !Array.isArray(parsed) && typeof parsed === 'object') {
          if (parsed.luminaire12NC) {
            return String(parsed.luminaire12NC).trim();
          }

          if (Array.isArray(parsed.bomFiles) && parsed.bomFiles.length > 0) {
            return String(parsed.bomFiles[0]?.luminaire12NC || '').trim();
          }
        }

        if (Array.isArray(parsed) && parsed.length > 0) {
          return String(parsed[0]?.luminaire12NC || '').trim();
        }
      } catch (error) {
        console.warn('Failed to parse uploaded BOM content for luminaire 12NC:', error);
      }

      return '';
    };

    Object.entries(localBomData || {}).forEach(([batchId, batch]) => {
      const expected12NC = String(batch12NCMap[batchId] || batchId || '').trim();
      if (!expected12NC) {
        return;
      }

      let selectedFile = null;
      let selectedFileName = '';
      if (batch?.selectedBOMFileId && filesById[batch.selectedBOMFileId]) {
        selectedFile = filesById[batch.selectedBOMFileId];
        selectedFileName = selectedFile.name;
      } else if (batch?.selectedBOMFile) {
        selectedFileName = batch.selectedBOMFile;
      }

      if (!selectedFileName) {
        return;
      }

      const selectedFile12NC = extract12NCFromBOMFileName(selectedFileName);
      const selectedLuminaire12NC = getUploadedLuminaire12NC(batch, selectedFile);
      const mismatchMessages = [];

      if (!selectedFile12NC) {
        mismatchMessages.push('BOM filename must follow BOM-(12NC)-Timestamp.');
      } else if (selectedFile12NC !== expected12NC) {
        mismatchMessages.push(`BOM filename 12NC ${selectedFile12NC} does not match CSV 12NC ${expected12NC}.`);
      }

      if (selectedLuminaire12NC && selectedLuminaire12NC !== expected12NC) {
        mismatchMessages.push(`BOM luminaire 12NC ${selectedLuminaire12NC} does not match CSV 12NC ${expected12NC}.`);
      }

      if (mismatchMessages.length > 0) {
        warnings[batchId] = mismatchMessages.join(' ');
      }
    });

    return warnings;
  };

  const batch12NCMismatchWarnings = getBatch12NCMismatchWarnings();
  const mismatchWarningEntries = Object.entries(batch12NCMismatchWarnings);

  // Auto-extract 12NC when dialog opens
  const handleCreateBOMClick = () => {
    // Get all available 12NCs and set the first one as default
    const available12NCs = getAllUnique12NCs();
    if (available12NCs.length > 0) {
      setLuminaire12NC(available12NCs[0]);
    }
    setShowCreateBOMDialog(true);
  };

  const createBOMFile = async () => {
    const validEntries = newBOMEntries.filter(entry => entry.name && entry.materialtype);

    if (newBOMEntries.length === 0 || validEntries.length !== newBOMEntries.length) {
      setUploadToast({
        show: true,
        message: 'Please fill all required BOM entry fields (Material Type and Name are required for every entry).',
        type: 'error'
      });
      return;
    }

    if (!luminaire12NC.trim()) {
      setUploadToast({
        show: true,
        message: 'Please enter the Luminaire 12NC for file naming.',
        type: 'error'
      });
      return;
    }

    if (!isValidLuminaire12NC(luminaire12NC)) {
      setUploadToast({
        show: true,
        message: 'Luminaire 12NC is required and must be exactly 12 digits.',
        type: 'error'
      });
      return;
    }

    if (!newBOMEffectiveDate) {
      setUploadToast({
        show: true,
        message: 'Please select the Effective Date.',
        type: 'error'
      });
      return;
    }

    if (!isValidISODate(newBOMEffectiveDate)) {
      setUploadToast({
        show: true,
        message: 'Please select a valid Effective Date.',
        type: 'error'
      });
      return;
    }

    if (newBOMEffectiveDate > maxEffectiveDate) {
      setUploadToast({
        show: true,
        message: `Effective Date cannot be later than ${maxEffectiveDate}.`,
        type: 'error'
      });
      return;
    }

    // Create BOM file name using luminaire 12NC and timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const bomFileName = `BOM-${luminaire12NC.trim()}-${timestamp}`;

    // Create the BOM file object
    const newBOMFile = {
      id: Date.now().toString(),
      name: bomFileName,
      luminaire12NC: luminaire12NC.trim(),
      effectiveDate: newBOMEffectiveDate,
      bom: [...validEntries]
    };

    try {
      const fileBlob = new Blob([JSON.stringify(newBOMFile, null, 2)], { type: 'application/json' });
      await bomAPI.uploadBOMFile(fileBlob, `${bomFileName}.json`);
    } catch (uploadError) {
      console.error('Failed to upload BOM file:', uploadError);
      setUploadToast({
        show: true,
        message: uploadError?.message || 'Failed to upload BOM file to /bom API.',
        type: 'error'
      });
      return;
    }

    // Reset form and close dialog
    setNewBOMEntries([{ name: '', materialtype: '', type: '' }]);
    setLuminaire12NC('');
    setNewBOMEffectiveDate('');
    setShowCreateBOMDialog(false);

    setUploadToast({
      show: true,
      message: `BOM file "${bomFileName}.json" uploaded successfully with ${validEntries.length} entries.`,
      type: 'success'
    });
  };

  const handleCloseBOMDialog = () => {
    // Always clear the form so reopening starts fresh, even after a failed upload.
    setNewBOMEntries([{ name: '', materialtype: '', type: '' }]);
    setLuminaire12NC('');
    setNewBOMEffectiveDate('');
    setSelectedSupportedFactoryCode('');
    setIsCreateBOMFromTable(false);
    setCreateBOMStep(1);
    setCreateBOMResult({ status: 'idle', fileName: '', errorMessage: '' });
    setShowCreateBOMDialog(false);
  };

  const handleBOMUpload = async () => {
    const validEntries = newBOMEntries.filter(e => e.name && e.materialtype);
    setIsSubmittingBOM(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const bomFileName = `BOM-${luminaire12NC.trim()}-${timestamp}`;
    const resolvedFactoryCode = hasMultipleSupportedFactoryCodes
      ? String(selectedSupportedFactoryCode || '').trim()
      : getDefaultFactoryCode();

    if (!resolvedFactoryCode) {
      setCreateBOMResult({
        status: 'failed',
        fileName: '',
        errorMessage: 'Factory code is required from Details API before creating BOM.',
      });
      setIsSubmittingBOM(false);
      setCreateBOMStep(4);
      return;
    }

    const newBOMFile = {
      id: Date.now().toString(),
      name: bomFileName,
      luminaire12NC: luminaire12NC.trim(),
      effectiveDate: newBOMEffectiveDate,
      bom: [...validEntries],
    };
    try {
      const fileBlob = new Blob([JSON.stringify(newBOMFile, null, 2)], { type: 'application/json' });
      await bomAPI.uploadBOMFile(fileBlob, `${bomFileName}.json`, resolvedFactoryCode);
      setCreateBOMResult({ status: 'success', fileName: bomFileName, errorMessage: '' });
      if (typeof onUploadSuccess === 'function') {
        await Promise.resolve(onUploadSuccess(`BOM file "${bomFileName}.json" uploaded successfully.`));
      }
    } catch (uploadError) {
      setCreateBOMResult({ status: 'failed', fileName: '', errorMessage: uploadError?.message || 'Failed to upload BOM file.' });
    } finally {
      setIsSubmittingBOM(false);
      setCreateBOMStep(4);
    }
  };

  const uploadFilesByType = async (files, type) => {
    if (!Array.isArray(files) || files.length === 0) {
      return null;
    }

    const uploadResult = type === 'csv'
      ? await bomAPI.uploadCSVFiles(files)
      : await bomAPI.uploadBOMPackageFiles(files);

    const totalSizeBytes = files.reduce((sum, file) => sum + (file?.size || 0), 0);
    const fileNames = files.map((file) => file.name).join(', ');

    return {
      type,
      mode: 'multipart',
      uploadedCount: files.length,
      fileName: fileNames,
      sizeKB: (totalSizeBytes / 1024).toFixed(2),
      response: uploadResult,
    };
  };

  const getFactoryCodesFromCSVData = () => {
    if (!csvData || !Array.isArray(csvData.originalCsvData)) {
      return [];
    }

    const factoryCodes = csvData.originalCsvData
      .map((row) => String(row?.extractedFactoryCode || '').trim())
      .filter(Boolean);

    return Array.from(new Set(factoryCodes));
  };

  const getFactoryCodesFromFactoryRowsForUploadedBOM = () => {
    if (!Array.isArray(factoryBOMRows) || factoryBOMRows.length === 0) {
      return [];
    }

    const uploadedTwelveNCs = new Set(
      globalBOMFiles
        .map((bomFile) => String(resolveBOMFile12NC(bomFile) || '').trim())
        .filter(Boolean)
    );

    if (uploadedTwelveNCs.size === 0) {
      return [];
    }

    const rowFactoryCodes = factoryBOMRows
      .filter((row) => {
        const twelveNC = String(row?.twelveNC || '').trim();
        const factoryCode = String(row?.factoryCode || '').trim();
        return uploadedTwelveNCs.has(twelveNC) && !!factoryCode;
      })
      .map((row) => String(row?.factoryCode || '').trim())
      .filter(Boolean);

    return Array.from(new Set(rowFactoryCodes));
  };

  const hasFactoryCodeResolvedForUploadedBOM = useMemo(() => {
    const uploadedTwelveNCs = new Set(
      globalBOMFiles
        .map((bomFile) => String(resolveBOMFile12NC(bomFile) || '').trim())
        .filter(Boolean)
    );

    if (uploadedTwelveNCs.size === 0) {
      return false;
    }

    const hasFactoryCodeFromServiceTags = Array.isArray(csvData?.originalCsvData)
      && csvData.originalCsvData.some((row) => {
        const twelveNC = String(row?.extracted12NC || '').trim();
        const factoryCode = String(row?.extractedFactoryCode || '').trim();
        return uploadedTwelveNCs.has(twelveNC) && !!factoryCode;
      });

    if (hasFactoryCodeFromServiceTags) {
      return true;
    }

    return Array.isArray(factoryBOMRows) && factoryBOMRows.some((row) => {
      const twelveNC = String(row?.twelveNC || '').trim();
      const factoryCode = String(row?.factoryCode || '').trim();
      return uploadedTwelveNCs.has(twelveNC) && !!factoryCode;
    });
  }, [csvData, factoryBOMRows, globalBOMFiles, resolveBOMFile12NC]);

  useEffect(() => {
    if (normalizedSupportedFactoryCodes.length === 1) {
      setSelectedSupportedFactoryCode(normalizedSupportedFactoryCodes[0]);
      return;
    }

    if (normalizedSupportedFactoryCodes.length > 1 && selectedSupportedFactoryCode && !normalizedSupportedFactoryCodes.includes(selectedSupportedFactoryCode)) {
      setSelectedSupportedFactoryCode('');
    }

    if (normalizedSupportedFactoryCodes.length === 0) {
      setSelectedSupportedFactoryCode('');
    }
  }, [normalizedSupportedFactoryCodes, selectedSupportedFactoryCode]);

  const getFactoryCodesForBOMUpload = () => {
    const csvFactoryCodes = getFactoryCodesFromCSVData();
    if (csvFactoryCodes.length > 0) {
      return csvFactoryCodes;
    }

    const factoryRowCodes = getFactoryCodesFromFactoryRowsForUploadedBOM();
    if (factoryRowCodes.length > 0) {
      return factoryRowCodes;
    }

    if (supportedFactoryCodes.length === 1) {
      return supportedFactoryCodes;
    }

    if (supportedFactoryCodes.length > 1 && selectedSupportedFactoryCode) {
      return [selectedSupportedFactoryCode];
    }

    return [];
  };

  const getBatchIDsForTwelveNC = (twelveNC, manufacturingDate = '') => {
    if (!csvData || !Array.isArray(csvData.originalCsvData)) {
      return [];
    }

    const normalizedManufacturingDate = String(manufacturingDate || '').trim();

    const batchIds = csvData.originalCsvData
      .filter((row) => {
        const row12NC = String(row?.extracted12NC || '').trim();
        const rowManufacturingDate = String(row?.extractedManufacturingDate || '').trim();

        if (row12NC !== String(twelveNC).trim()) {
          return false;
        }

        if (!normalizedManufacturingDate) {
          return true;
        }

        return rowManufacturingDate === normalizedManufacturingDate;
      })
      .map((row) => String(row?.extractedBatchId || '').trim())
      .filter(Boolean);

    return Array.from(new Set(batchIds)).sort();
  };

  const toggleRowExpanded = (rowKey) => {
    setExpandedRows((prevSet) => {
      const newSet = new Set(prevSet);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  const handleUploadCSV = async () => {
    if (uploadedCSVFiles.length === 0 && uploadedBOMFiles.length === 0) {
      setUploadToast({
        show: true,
        message: 'Upload at least one CSV or BOM file before submitting.',
        type: 'error'
      });
      return;
    }

    try {
      const uploadButton = document.querySelector('[data-upload-button="true"]');
      if (uploadButton) {
        uploadButton.disabled = true;
        uploadButton.textContent = 'Uploading...';
      }

      const results = [];

      if (uploadedCSVFiles.length > 0) {
        const csvResult = await uploadFilesByType(uploadedCSVFiles, 'csv');
        if (csvResult) {
          results.push(csvResult);
        }
      }

      if (uploadedBOMFiles.length > 0) {
        const factoryCodes = getFactoryCodesForBOMUpload();

        if (factoryCodes.length === 0) {
          if (supportedFactoryCodes.length > 1) {
            throw new Error('Multiple supported factory codes are available. Please select one factory code before uploading BOM files.');
          }

          throw new Error('No factory codes found. Upload a service-tag CSV or use a single supported factory from Details API.');
        }

        for (const factoryCode of factoryCodes) {
          const uploadResult = await bomAPI.uploadBOMPackageFilesByFactory(factoryCode, uploadedBOMFiles);
          const totalSizeBytes = uploadedBOMFiles.reduce((sum, file) => sum + (file?.size || 0), 0);
          const fileNames = uploadedBOMFiles.map((file) => file.name).join(', ');

          results.push({
            type: `bom:${factoryCode}`,
            mode: 'multipart',
            uploadedCount: uploadedBOMFiles.length,
            fileName: fileNames,
            sizeKB: (totalSizeBytes / 1024).toFixed(2),
            response: uploadResult,
          });
        }
      }

      const uploadSummaryParts = [];

      if (uploadedCSVFiles.length > 0) {
        uploadSummaryParts.push(`${uploadedCSVFiles.length} factory file(s)`);
      }

      if (uploadedBOMFiles.length > 0) {
        uploadSummaryParts.push(`${uploadedBOMFiles.length} BOM file(s)`);
      }

      const successMessage = uploadSummaryParts.length > 0
        ? `Successfully uploaded ${uploadSummaryParts.join(' and ')}.`
        : 'Upload completed successfully.';

      if (typeof onUploadSuccess === 'function') {
        onUploadSuccess(successMessage);
        return;
      }

      setUploadToast({
        show: true,
        message: `✅ ${successMessage}`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error during upload process:', error);
      setUploadToast({
        show: true,
        message: `❌ Upload failed! Error. Please check your internet connection and try again.`,
        type: 'error'
      });
    } finally {
      const uploadButton = document.querySelector('[data-upload-button="true"]');
      if (uploadButton) {
        uploadButton.disabled = false;
        uploadButton.textContent = 'Upload selected files';
      }
    }
  };
  const renderCreateBOMDialog = () => {
    if (!showCreateBOMDialog) return null;

    const bomSteps = [
      { id: 1, title: 'Enter BOM Details' },
      { id: 2, title: 'Add Materials' },
      { id: 3, title: 'Review BOM' },
      { id: 4, title: 'Summary' },
    ];

    const stepIndicator = (
      <div style={{ fontSize: '0.9rem', marginBottom: '12px', padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', backgroundColor: '#f8f9fa' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {bomSteps.map((step, index) => {
            const isActive = createBOMStep === step.id;
            const isCompleted = createBOMStep > step.id;
            return (
              <React.Fragment key={step.id}>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <div style={isActive ? {
                    padding: '6px 14px', borderRadius: '10px',
                    background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  } : { display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className={`badge ${isActive ? 'bg-primary' : isCompleted ? 'bg-success' : 'bg-secondary'}`}>{step.id}</span>
                    <span style={{ fontWeight: isActive ? 700 : 400, color: isActive ? '#0d6efd' : '#6c757d', fontSize: '0.9rem' }}>{step.title}</span>
                  </div>
                </div>
                {index < bomSteps.length - 1 && (
                  <div style={{ flex: 0.2, display: 'flex', justifyContent: 'center' }}>
                    <span style={{ color: '#adb5bd' }}>→</span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );

    const renderStep1 = () => (
      <div className="row">
        <div className="col-md-8">
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold bom-form-label-strong">Luminaire 12NC *</Form.Label>
            {isStandaloneMode || !csvData ? (
              <Form.Control
                type="text"
                className="bom-form-control"
                value={luminaire12NC}
                onChange={handleLuminaire12NCChange}
                placeholder="Enter Luminaire 12NC (12 digits)"
                inputMode="numeric"
                pattern="\d{12}"
                maxLength={12}
                readOnly={isCreateBOMFromTable}
              />
            ) : (
              <Form.Select
                className="bom-form-control"
                value={luminaire12NC}
                onChange={(e) => setLuminaire12NC(e.target.value)}
                disabled={isCreateBOMFromTable || getAllUnique12NCs().length === 0}
              >
                {getAllUnique12NCs().length === 0 ? (
                  <option value="">No 12NCs found in uploaded file</option>
                ) : (
                  <>
                    <option value="">Select a Luminaire 12NC</option>
                    {getAllUnique12NCs().map((nc12, idx) => (
                      <option key={idx} value={nc12}>{nc12}</option>
                    ))}
                  </>
                )}
              </Form.Select>
            )}
            <Form.Text className="text-muted">
              {isStandaloneMode || !csvData
                ? 'Enter the 12-digit Luminaire 12NC for the BOM file.'
                : `Select from ${getAllUnique12NCs().length} 12NCs extracted from the uploaded CSV.`}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold bom-form-label-strong">Effective Date *</Form.Label>
            <Form.Control
              type="date"
              className="bom-form-control"
              value={newBOMEffectiveDate}
              onChange={(e) => setNewBOMEffectiveDate(e.target.value)}
              max={maxEffectiveDate}
            />
            <Form.Text className="text-muted">Select the BOM effective date (up to {maxEffectiveDate}).</Form.Text>
          </Form.Group>
          {hasMultipleSupportedFactoryCodes ? (
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold bom-form-label-strong">Factory Code *</Form.Label>
              <Form.Select
                className="bom-form-control"
                value={selectedSupportedFactoryCode}
                onChange={(event) => setSelectedSupportedFactoryCode(event.target.value)}
                disabled={isCreateBOMFromTable}
              >
                <option value="">Select Factory Code</option>
                {normalizedSupportedFactoryCodes.map((factoryCode) => (
                  <option key={factoryCode} value={factoryCode}>{factoryCode}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {isCreateBOMFromTable
                  ? 'Factory Code is preselected from the chosen row and cannot be changed here.'
                  : 'Multiple factory codes are supported. Select one to create BOM.'}
              </Form.Text>
            </Form.Group>
          ) : null}
        </div>
        <div className="col-md-4">
          <div className="card bg-light border-0">
            <div className="card-body">
              <h6 className="fw-bold mb-3">BOM File Details</h6>
              <ul className="list-unstyled">
                <li className="mb-2"><small>Luminaire 12NC uniquely identifies the product for the BOM.</small></li>
                <li className="mb-2"><small>Effective Date determines when the BOM becomes active.</small></li>
                <li className="mb-2"><small>Factory Code is resolved from the Details API.</small></li>
                <li><small>A file will be generated as <code>BOM-{'{12NC}'}-timestamp.json</code>.</small></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );

    const renderStep2 = () => (
      <div className="row">
        <div className="col-md-8">
          {newBOMEntries.map((entry, index) => (
            <div key={index} className="bom-entry-card mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <Form.Label className="small fw-bold bom-form-label-strong">Material Type *</Form.Label>
                  <Form.Select size="sm" className="bom-form-control" value={entry.materialtype} onChange={(e) => updateNewBOMEntry(index, 'materialtype', e.target.value)}>
                    <option value="">Select Type</option>
                    {MATERIAL_TYPE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </Form.Select>
                </div>
                <div className="col-md-3">
                  <Form.Label className="small fw-bold bom-form-label-strong">Material 12NC</Form.Label>
                  <Form.Control size="sm" type="text" className="bom-form-control" value={entry.type} onChange={(e) => updateNewBOMEntry(index, 'type', e.target.value)} placeholder="12NC value" />
                </div>
                <div className="col-md-4">
                  <Form.Label className="small fw-bold bom-form-label-strong">Material Name *</Form.Label>
                  <Form.Control size="sm" type="text" className="bom-form-control" value={entry.name} onChange={(e) => updateNewBOMEntry(index, 'name', e.target.value)} placeholder="Component name" />
                </div>
                <div className="col-md-2 d-grid">
                  {newBOMEntries.length > 1 && (
                    <Button className="bom-btn-danger" size="sm" onClick={() => removeNewBOMEntry(index)}>-</Button>
                  )}
                </div>
              </div>
            </div>
          ))}
          <Button className="bom-btn-outline" onClick={addNewBOMEntry}>+ Add Material</Button>
        </div>
        <div className="col-md-4">
          <div className="card bg-light border-0">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Material Structure</h6>
              <ul className="list-unstyled">
                <li className="mb-2"><small><strong>Material Type</strong>: Category (e.g. Driver, Optic, LED Board).</small></li>
                <li className="mb-2"><small><strong>Material 12NC</strong>: Optional component identifier code.</small></li>
                <li><small><strong>Material Name</strong>: Descriptive name of the component.</small></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );

    const renderStep3 = () => {
      const validEntries = newBOMEntries.filter(e => e.name && e.materialtype);
      const materialTypeCount = new Set(validEntries.map(e => e.materialtype).filter(Boolean)).size;
      return (
        <div>
          <div className="row mb-3">
            <div className="col-md-3 mb-2">
              <div className="card text-center h-100">
                <div className="card-body" style={{ padding: '8px 12px' }}>
                  <small className="text-muted d-block mb-1">Luminaire 12NC</small>
                  <p className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{luminaire12NC || '—'}</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="card text-center h-100">
                <div className="card-body" style={{ padding: '8px 12px' }}>
                  <small className="text-muted d-block mb-1">Effective Date</small>
                  <p className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{newBOMEffectiveDate || '—'}</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="card text-center h-100">
                <div className="card-body" style={{ padding: '8px 12px' }}>
                  <small className="text-muted d-block mb-1">Factory Code</small>
                  <p className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>
                    {(hasMultipleSupportedFactoryCodes ? selectedSupportedFactoryCode : getDefaultFactoryCode()) || '—'}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="card text-center h-100">
                <div className="card-body" style={{ padding: '8px 12px' }}>
                  <small className="text-muted d-block mb-1">Total Materials</small>
                  <h5 className="mb-0" style={{ color: '#00e487' }}>{validEntries.length}</h5>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="card text-center h-100">
                <div className="card-body" style={{ padding: '8px 12px' }}>
                  <small className="text-muted d-block mb-1">Material Types</small>
                  <h5 className="mb-0" style={{ color: '#00e487' }}>{materialTypeCount}</h5>
                </div>
              </div>
            </div>
          </div>
          <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>BOM Materials Preview</h6>
          <div className="table-responsive" style={{ borderRadius: '16px', border: '1px solid #dee2e6', overflow: 'hidden', marginBottom: '1rem' }}>
            <table className="table table-sm table-hover align-middle mb-0" style={{ borderCollapse: 'collapse' }}>
              <thead className="table-light" style={{ borderBottom: '2px solid #dee2e6' }}>
                <tr style={{ borderBottom: 'none' }}>
                  <th style={{ borderBottom: 'none' }}>#</th>
                  <th style={{ borderBottom: 'none' }}>Material Type</th>
                  <th style={{ borderBottom: 'none' }}>Material 12NC</th>
                  <th style={{ borderBottom: 'none' }}>Material Name</th>
                </tr>
              </thead>
              <tbody>
                {validEntries.length === 0 ? (
                  <tr style={{ borderBottom: 'none' }}><td colSpan={4} className="text-center text-muted py-3" style={{ borderRight: 'none' }}>No complete material entries yet.</td></tr>
                ) : (
                  validEntries.map((entry, i) => (
                    <tr key={i} style={{ borderBottom: 'none' }}>
                      <td style={{ borderRight: 'none' }}>{i + 1}</td>
                      <td style={{ borderRight: 'none' }}><span className="badge bg-secondary">{entry.materialtype}</span></td>
                      <td style={{ borderRight: 'none' }}><code>{entry.type}</code></td>
                      <td style={{ borderRight: 'none' }}>{entry.name}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    const renderStep4 = () => {
      const isSuccess = createBOMResult.status === 'success';
      return (
        <div>
          <div className="row mb-3">
            <div className="col-md-4 mb-2">
              <div className="card text-center h-100">
                <div className="card-body" style={{ padding: '8px 12px' }}>
                  <small className="text-muted d-block mb-1">BOM File</small>
                  <p className="mb-0 fw-bold" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    {createBOMResult.fileName ? `${createBOMResult.fileName}.json` : '—'}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-2">
              <div className="card text-center h-100">
                <div className="card-body" style={{ padding: '8px 12px' }}>
                  <small className="text-muted d-block mb-1">Final Status</small>
                  <h6 className={isSuccess ? 'text-success mb-0' : 'text-danger mb-0'}>{isSuccess ? 'Completed' : 'Failed'}</h6>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-2">
              <div className="card text-center h-100">
                <div className="card-body" style={{ padding: '8px 12px' }}>
                  <small className="text-muted d-block mb-1">Created At</small>
                  <p className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{new Date().toLocaleTimeString()}</p>
                  <small className="text-muted" style={{ fontSize: '0.8rem' }}>{new Date().toLocaleDateString()}</small>
                </div>
              </div>
            </div>
          </div>
          {isSuccess ? (
            <>
              <div className="alert mb-3" style={{ backgroundColor: '#d4edda', color: '#155724', border: 'none' }}>
                <strong>BOM file created successfully.</strong>
              </div>
              <div className="card mt-2">
                <div className="card-header bg-light"><h6 className="mb-0">Summary</h6></div>
                <div className="card-body">
                  <ul className="mb-0">
                    <li>Luminaire 12NC: <strong>{luminaire12NC}</strong></li>
                    <li>Effective Date: <strong>{newBOMEffectiveDate}</strong></li>
                    <li>Total Materials: <strong>{newBOMEntries.filter(e => e.name && e.materialtype).length}</strong></li>
                    <li>Material Types: <strong>{new Set(newBOMEntries.map(e => e.materialtype).filter(Boolean)).size}</strong></li>
                  </ul>
                </div>
              </div>
            </>
          ) : (
            <div className="alert alert-danger">
              <strong>Failed to create BOM file.</strong>
              <div className="mt-1">{createBOMResult.errorMessage || 'Please try again.'}</div>
            </div>
          )}
        </div>
      );
    };

    const handleNextStep1 = () => {
      if (!luminaire12NC.trim()) {
        setUploadToast({ show: true, message: 'Please enter or select the Luminaire 12NC.', type: 'error' });
        return;
      }
      if (!isValidLuminaire12NC(luminaire12NC)) {
        setUploadToast({ show: true, message: 'Luminaire 12NC must be exactly 12 digits.', type: 'error' });
        return;
      }
      if (!newBOMEffectiveDate) {
        setUploadToast({ show: true, message: 'Please select the Effective Date.', type: 'error' });
        return;
      }
      if (hasMultipleSupportedFactoryCodes && !String(selectedSupportedFactoryCode || '').trim()) {
        setUploadToast({ show: true, message: 'Please select the Factory Code from Details API.', type: 'error' });
        return;
      }
      setCreateBOMStep(2);
    };

    const handleNextStep2 = () => {
      const validEntries = newBOMEntries.filter(e => e.name && e.materialtype);
      if (newBOMEntries.length === 0 || validEntries.length !== newBOMEntries.length) {
        setUploadToast({ show: true, message: 'Please fill in all required material fields (Material Type and Name).', type: 'error' });
        return;
      }
      setCreateBOMStep(3);
    };

    return (
      <div className="modal fade show d-block bom-modal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content bom-modal-content">
            <div className="modal-header bom-modal-header">
              <h5 className="modal-title bom-modal-title">Create Bill of Material</h5>
              <button type="button" className="btn-close btn-close-white" onClick={handleCloseBOMDialog}></button>
            </div>
            <div className="modal-body bom-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {uploadToast.show && uploadToast.type === 'error' && (
                <div className="alert alert-danger mb-3" role="alert">
                  <strong>Error:</strong> {uploadToast.message}
                </div>
              )}
              {stepIndicator}
              {createBOMStep === 1 && renderStep1()}
              {createBOMStep === 2 && renderStep2()}
              {createBOMStep === 3 && renderStep3()}
              {createBOMStep === 4 && renderStep4()}
            </div>
            <div className="modal-footer bom-modal-footer">
              {createBOMStep === 1 && (
                <>
                  <Button variant="outline-secondary" onClick={handleCloseBOMDialog}>Cancel</Button>
                  <Button className="bom-btn-primary" onClick={handleNextStep1}>Next</Button>
                </>
              )}
              {createBOMStep === 2 && (
                <>
                  <Button variant="outline-secondary" onClick={() => setCreateBOMStep(1)}>Back</Button>
                  <Button className="bom-btn-primary" onClick={handleNextStep2}>Review BOM</Button>
                </>
              )}
              {createBOMStep === 3 && (
                <>
                  <Button variant="outline-secondary" onClick={() => setCreateBOMStep(2)} disabled={isSubmittingBOM}>Back</Button>
                  <Button className="bom-btn-primary" onClick={handleBOMUpload} disabled={isSubmittingBOM}>
                    {isSubmittingBOM ? 'Creating...' : 'Confirm & Create'}
                  </Button>
                </>
              )}
              {createBOMStep === 4 && (
                <Button variant="secondary" onClick={handleCloseBOMDialog}>Close</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (dialogOnly) {
    return (
      <>
        {uploadToast.show && uploadToast.type === 'success' && (
          <div className={`bom-floating-toast bom-floating-toast-${uploadToast.type}`}>
            <div className="bom-floating-toast-content">
              {uploadToast.type === 'success' ? <strong>✅ Success</strong> : <strong>❌ Error</strong>}
              <p>{uploadToast.message}</p>
            </div>
          </div>
        )}
        {renderCreateBOMDialog()}
      </>
    );
  }

  return (
    <div className={containerClassName}>
      {/* Upload Floating Toast */}
      {uploadToast.show && uploadToast.type === 'success' && (
        <div className={`bom-floating-toast bom-floating-toast-${uploadToast.type}`}>
          <div className="bom-floating-toast-content">
            {uploadToast.type === 'success' ? <strong>✅ Success</strong> : <strong>❌ Error</strong>}
            <p>{uploadToast.message}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={mainContentClassName}>
        <div className="container-fluid">
          {uploadToast.show && uploadToast.type === 'error' && (
            <div className="alert alert-danger mb-3" role="alert">
              <strong>Error:</strong> {uploadToast.message}
            </div>
          )}

          {showManagementWorkspace && !previewOnly && (
            <h5 className="bom-section-title">Factory File Management</h5>
          )}

          {/* Only show stats cards and batch management in non-standalone mode */}
          {showManagementWorkspace && (
            <>
              {hasUploadedData && (
                <>
                  <div className="bom-search-wrapper">
                    <Form.Group className="mb-0" style={{ flex: 1, marginRight: '10px' }}>
                      <Form.Control
                        type="text"
                        placeholder="🔍 Search by 12NC, date, factory code, status, or BOM file name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bom-search-input-field"
                      />
                    </Form.Group>
                    {searchTerm && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setSearchTerm('')}
                        style={{ borderRadius: '6px', marginRight: '10px' }}
                      >
                        ✕ Clear
                      </Button>
                    )}
                    <small className="text-muted" style={{ marginRight: '20px', whiteSpace: 'nowrap' }}>
                      {filteredTableRows.length === tableRows.length
                        ? `${filteredTableRows.length} result${filteredTableRows.length !== 1 ? 's' : ''}`
                        : `${filteredTableRows.length} of ${tableRows.length} results`}
                    </small>
                    {showCreateBOMButton && (
                      <Button
                        className="bom-btn-primary"
                        size="sm"
                        onClick={handleCreateBOMClick}
                      >
                        Create BOM File
                      </Button>
                    )}
                  </div>
                  <Card className="bom-main-card">
                    <Card.Header className="bom-header"></Card.Header>
                    <Card.Body className="p-0">
                      <Table className="bom-table" responsive>
                        <thead>
                          <tr>
                            <th style={{ width: '3%' }}></th>
                            <th style={{ width: '19%' }}>Unique 12NC</th>
                            <th style={{ width: '19%' }}>Manufacturing Date</th>
                            <th style={{ width: '15%' }}>Factory Code</th>
                            <th style={{ width: '16%' }}>Status</th>
                            <th style={{ width: '28%' }}>REMARKS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTableRows.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center text-muted py-4">
                                <em>{searchTerm ? 'No results found. Try adjusting your search.' : 'No data available.'}</em>
                              </td>
                            </tr>
                          ) : (
                            filteredTableRows.map((row) => {
                              const isExpanded = expandedRows.has(row.rowKey);
                              const batchIds = getBatchIDsForTwelveNC(row.twelveNC, row.manufacturingDateRaw);
                              const shouldAlwaysShowMutedRemark = row.statusMessage.startsWith('Upload Factory File to make use of this BOM')
                                || row.statusMessage.startsWith('Matched uploaded BOM effective date');

                              return (
                                <React.Fragment key={row.rowKey}>
                                  <tr style={{ cursor: 'pointer' }} onClick={() => toggleRowExpanded(row.rowKey)}>
                                    <td className="text-center">
                                      <span style={{ fontSize: '14px' }}>{isExpanded ? '▼' : '▶'}</span>
                                    </td>
                                    <td>
                                      <span className="bom-batch-id">{row.twelveNC}</span>
                                    </td>
                                    <td>
                                      <span className="bom-batch-id">{row.manufacturingDate}</span>
                                    </td>
                                    <td>
                                      {row.allowFactorySelection ? (
                                        <Form.Select
                                          size="sm"
                                          value={row.factoryCode || ''}
                                          onClick={(event) => event.stopPropagation()}
                                          onChange={(event) => handleManualFactoryCodeChange(row.twelveNC, event.target.value)}
                                        >
                                          <option value="">Select factory code</option>
                                          {supportedFactoryCodes.map((factoryCodeOption) => (
                                            <option key={factoryCodeOption} value={factoryCodeOption}>{factoryCodeOption}</option>
                                          ))}
                                        </Form.Select>
                                      ) : row.factoryCode ? (
                                        <span className="bom-batch-id">{row.factoryCode}</span>
                                      ) : (
                                        <span className="text-muted">Pending</span>
                                      )}
                                    </td>
                                    <td>
                                      {row.bomFromAPI && (
                                        <Badge bg="success">✓ Yes</Badge>
                                      )}
                                      {row.selectedBOMFile ? (
                                        <>
                                          <Badge className="bom-badge ms-2" bg="success">{row.bomEntries.length} material(s)</Badge>
                                          <div className="bom-batch-selection mt-2">
                                            <div className="bom-batch-selected-name" title={row.selectedBOMFile}>
                                              {row.selectedBOMFile}
                                            </div>
                                            {row.uploadedBOMCount > 1 && (
                                              <Badge bg="warning" text="dark" className="ms-2">{row.uploadedBOMCount} BOM uploaded</Badge>
                                            )}
                                          </div>
                                        </>
                                      ) : !row.bomFromAPI ? (
                                        <Badge bg="danger">✗ No</Badge>
                                      ) : null}
                                      {!row.selectedBOMFile && row.uploadedBOMCount > 1 && (
                                        <div className="mt-2">
                                          <Badge bg="warning" text="dark">{row.uploadedBOMCount} BOM uploaded</Badge>
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      {shouldAlwaysShowMutedRemark ? (
                                        <span className="text-muted">{row.statusMessage}</span>
                                      ) : row.hasFactoryDateMismatch ? (
                                        <span className="text-warning fw-semibold">{row.statusMessage}</span>
                                      ) : row.bomFromAPI || row.selectedBOMFile ? (
                                        <span className="text-success fw-semibold">{row.statusMessage}</span>
                                      ) : (
                                        <span className="text-muted">{row.statusMessage}</span>
                                      )}
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                      <td colSpan={6} className="p-3">
                                        <div className="ms-3">
                                          <h6 className="mb-2 fw-bold">Batch IDs for 12NC: {row.twelveNC}</h6>
                                          {batchIds.length > 0 ? (
                                            <div>
                                              {batchIds.map((batchId, idx) => (
                                                <div key={`${row.twelveNC}-${batchId}-${idx}`} className="ms-2 mb-1">
                                                  <Badge bg="info" className="me-2">{idx + 1}</Badge>
                                                  <span>{batchId}</span>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="text-muted">
                                              {/* <em>No Servicetags Uploaded Yet for this 12NC</em> */}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </>
              )}

              {showUploadSection && (
                <div className="bom-upload-section">
                  {mismatchWarningEntries.length > 0 && (
                    <div className="alert alert-danger mb-3 text-start">
                      <strong>BOM 12NC mismatch warnings</strong>
                      <ul className="mb-0 mt-2">
                        {mismatchWarningEntries.map(([batchId, warningMessage]) => (
                          <li key={batchId}>12NC {batchId}: {warningMessage}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {uploadedBOMFiles.length > 0 && getFactoryCodesFromCSVData().length === 0 && supportedFactoryCodes.length === 1 && (
                    <div className="mb-3 text-start text-muted small">
                      Using supported factory code from Details API: <strong>{supportedFactoryCodes[0]}</strong>
                    </div>
                  )}

                  <Button
                    className="bom-upload-btn"
                    size="lg"
                    onClick={handleUploadCSV}
                    disabled={uploadedCSVFiles.length === 0 && uploadedBOMFiles.length === 0}
                    data-upload-button="true"
                  >
                    Upload selected files
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Standalone Mode - Only Show Create BOM Button */}
          {isStandaloneMode && (
            <div className="text-center py-5">
              <Button
                className="bom-btn-primary"
                size="lg"
                onClick={handleCreateBOMClick}
              >
                Create BOM file
              </Button>
            </div>
          )}

          {/* Signify-style BOM Creation Dialog */}
          {renderCreateBOMDialog()}

        </div>
      </div>
    </div>
  );
};

export default BOMDataDisplay;